import { Link } from 'react-router-dom'
import { motion } from 'motion/react'
import type { Domain, Project } from '@/types/content'
import { useContent } from '@/content'
import { ROUTES } from '@/lib/paths'
import { Icon, Surface } from '@/components/ui'
import { initialsOf } from './initials'
import styles from './ProjectCaseStudy.module.css'

interface ProjectCaseStudyProps {
  project: Project
}

/**
 * Resolves a classification into the domain and category names it points at.
 *
 * Takes the live domains list rather than importing the compiled-in one, so
 * a domain renamed or added through the admin panel resolves correctly here
 * too — the same document the "Domains & categories" section itself edits.
 */
const describe = (domains: Domain[], domainId: string, categoryId: string) => {
  const domain = domains.find((item) => item.id === domainId)
  const category = domain?.categories.find((item) => item.id === categoryId)
  return { domain, category }
}

/** Reveals a section as it scrolls in, once. */
const revealProps = {
  initial: { opacity: 0, y: 18 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '0px 0px -80px 0px' },
  transition: { duration: 0.5, ease: [0.23, 1, 0.32, 1] as const },
}

/**
 * The body of a single case study.
 *
 * Route concerns (lookup, 404, page title) stay in the page component, so
 * this renders a project it is given and nothing more.
 *
 * ## Structure
 *
 * A banner, then a two-column split: the narrative on the left, a sticky
 * facts rail on the right. The rail holds the things a visitor scans for
 * rather than reads — who it was for, when, what it was built with — so they
 * stay on screen while the story scrolls past them.
 *
 * The domain and category chips link back into the filtered work index. Each
 * case study is therefore a way *into* the rest of the portfolio rather than
 * a dead end, which is the whole point of organising the work by domain.
 */
export const ProjectCaseStudy = ({ project }: ProjectCaseStudyProps) => {
  const { detail } = project
  const { domains } = useContent()

  /*
   * The banner prefers the wide reference image and falls back to the card
   * cover. A project with one picture should still look finished, and a
   * 4:3 cover cropped to a banner is better than an empty frame.
   */
  const banner = project.reference || project.cover
  const gallery = (project.gallery ?? []).filter((image) => image.src)

  return (
    <article className={styles.study}>
      {/* ---- Banner ------------------------------------------------------ */}
      <header className={styles.banner}>
        <div className={styles.bannerFrame}>
          {banner ? (
            <img
              src={banner}
              alt=""
              className={styles.bannerImage}
              fetchPriority="high"
              decoding="async"
            />
          ) : (
            /*
             * No cover yet. This used to render `<img src={undefined}>`,
             * which browsers show as broken alt text inside a 16:9 hole —
             * every case study on the site looked like a bug. The initials
             * mark is the same one the cards use.
             */
            <div className={styles.bannerFallback} aria-hidden="true">
              <span className={styles.bannerInitials}>
                {initialsOf(project.title)}
              </span>
            </div>
          )}
          <div className={styles.bannerVeil} aria-hidden="true" />
        </div>

        <div className={styles.bannerCopy}>
          <p className={`t-12 ${styles.eyebrow}`}>
            {project.category} <span aria-hidden="true">·</span> {project.year}
          </p>
          <div className={styles.titleRow}>
            {project.logo ? (
              <img
                src={project.logo}
                alt=""
                className={styles.logo}
                decoding="async"
              />
            ) : null}
            <h1 className={styles.title}>{project.title}</h1>
          </div>
          <p className={`t-16 ${styles.summary}`}>{project.summary}</p>
        </div>
      </header>

      <div className={styles.layout}>
        {/* ---- Narrative ------------------------------------------------- */}
        <div className={styles.main}>
          <motion.section className={styles.block} {...revealProps}>
            <h2 className={`t-12 ${styles.blockLabel}`}>Overview</h2>
            <p className={`t-18 ${styles.lede}`}>{detail.overview}</p>
          </motion.section>

          {detail.challengeGroups.map((group, groupIndex) => (
            <motion.section key={group.title} className={styles.block} {...revealProps}>
              <h2 className={`t-12 ${styles.blockLabel}`}>
                {groupIndex === 0 ? 'What it took' : group.title}
              </h2>
              {groupIndex === 0 && detail.challengeGroups.length > 1 ? (
                <h3 className={`t-22 ${styles.groupTitle}`}>{group.title}</h3>
              ) : null}

              <ol className={styles.challengeList}>
                {group.items.map((item, index) => (
                  <li key={item.term} className={styles.challenge}>
                    <span className={styles.challengeIndex} aria-hidden="true">
                      {String(index + 1).padStart(2, '0')}
                    </span>
                    <div className={styles.challengeBody}>
                      <h4 className={`t-16 ${styles.term}`}>{item.term}</h4>
                      <p className={styles.prose}>{item.description}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </motion.section>
          ))}

          {/* ---- Highlights ---------------------------------------------- */}
          {gallery.length > 0 ? (
            <motion.section className={styles.block} {...revealProps}>
              <h2 className={`t-12 ${styles.blockLabel}`}>Highlights</h2>
              <ul className={styles.gallery}>
                {gallery.map((image) => (
                  <li key={image.id || image.src} className={styles.shot}>
                    <figure className={styles.figure}>
                      <img
                        src={image.src}
                        /* The caption is the description, so repeating it as
                           alt text would have a screen reader say it twice.
                           With no caption it is the only description there
                           is, and the picture would otherwise be silent. */
                        alt={image.caption ? '' : `${project.title} highlight`}
                        className={styles.shotImage}
                        loading="lazy"
                        decoding="async"
                      />
                      {image.caption ? (
                        <figcaption className={`t-12 ${styles.caption}`}>
                          {image.caption}
                        </figcaption>
                      ) : null}
                    </figure>
                  </li>
                ))}
              </ul>
            </motion.section>
          ) : null}

          <motion.section {...revealProps}>
            <Surface pad={25} radius="card" className={styles.outcome}>
              <h2 className={`t-12 ${styles.blockLabel}`}>Outcome</h2>
              <p className={`t-18 ${styles.outcomeText}`}>{detail.conclusion}</p>
              {project.url ? (
                <a
                  href={project.url}
                  target="_blank"
                  rel="noreferrer noopener"
                  className={styles.outcomeLink}
                >
                  Visit the live site
                  <Icon name="arrowUpRight" size={16} />
                </a>
              ) : null}
            </Surface>
          </motion.section>
        </div>

        {/* ---- Facts rail ------------------------------------------------ */}
        <aside className={styles.rail} aria-label="Project details">
          <dl className={styles.facts}>
            <div className={styles.fact}>
              <dt className={`t-12 ${styles.factTerm}`}>Client</dt>
              <dd className={`t-14 ${styles.factValue}`}>{project.client}</dd>
            </div>
            <div className={styles.fact}>
              <dt className={`t-12 ${styles.factTerm}`}>Year</dt>
              <dd className={`t-14 ${styles.factValue}`}>{project.year}</dd>
            </div>
            <div className={styles.fact}>
              <dt className={`t-12 ${styles.factTerm}`}>Role</dt>
              <dd className={`t-14 ${styles.factValue}`}>{detail.service}</dd>
            </div>
          </dl>

          {/*
           * Domains and categories, as links into the filtered index. The
           * site is organised around these three domains; a case study that
           * did not say which one it belongs to would be the one place that
           * organisation broke down.
           */}
          <div className={styles.railBlock}>
            <h2 className={`t-12 ${styles.factTerm}`}>Domains</h2>
            <ul className={styles.domainList}>
              {project.classifications.map(({ domain: domainId, category }) => {
                const { domain, category: cat } = describe(domains, domainId, category)
                if (!domain) return null
                return (
                  <li key={`${domainId}-${category}`}>
                    <Link
                      to={`${ROUTES.projects}?domain=${domainId}&category=${category}`}
                      className={styles.chipLink}
                    >
                      <Icon name={domain.icon} size={16} />
                      <span className={styles.chipText}>
                        <span className={styles.chipName}>{domain.name}</span>
                        {cat ? (
                          <span className={styles.chipCategory}>{cat.name}</span>
                        ) : null}
                      </span>
                    </Link>
                  </li>
                )
              })}
            </ul>
          </div>

          {/* The stack was in the data all along and shown nowhere. */}
          {project.stack.length > 0 ? (
            <div className={styles.railBlock}>
              <h2 className={`t-12 ${styles.factTerm}`}>Built with</h2>
              <ul className={styles.chips}>
                {project.stack.map((item) => (
                  <li key={item} className={styles.chip}>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          {detail.disciplines.length > 0 ? (
            <div className={styles.railBlock}>
              <h2 className={`t-12 ${styles.factTerm}`}>Disciplines</h2>
              <ul className={styles.chips}>
                {detail.disciplines.map((discipline) => (
                  <li key={discipline} className={styles.chip}>
                    {discipline}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </aside>
      </div>
    </article>
  )
}
